import { Button, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useFieldArray } from 'react-hook-form';

import { Icon } from '@/components/Icon/Icon';

import { useTranslate } from '@/hooks/useTranslate';

import { getDataTestId } from '@/utils/browser';

import { ForeignNonTaxResidencySectionProps } from '../TaxInfoDrawer.interface';
import { getForeignTaxResidencyCountryOptions } from '../utils/countryOptions';
import { MAX_FOREIGN_NON_TAX_RESIDENCIES } from './constants';
import { ForeignNonTaxResidencyBlock } from './ForeignNonTaxResidencyBlock';
import { DEFAULT_FOREIGN_NON_TAX_RESIDENCY_BLOCK } from './utils';

import {
  TaxInfoAddBlockRow,
  TaxInfoQuestionLabel,
  TaxInfoSubSection,
} from '../TaxInfoDrawer.styled';

export function ForeignNonTaxResidencySection({
  control,
  errors,
  isPending,
  resetField,
  tenantCountryCode,
}: Readonly<ForeignNonTaxResidencySectionProps>) {
  const { t, country: translatedCountry } = useTranslate();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'foreignNonTaxResidencies',
  });

  const countryOptions = useMemo(
    () =>
      getForeignTaxResidencyCountryOptions(
        tenantCountryCode,
        translatedCountry,
      ),
    [tenantCountryCode, translatedCountry],
  );

  const handleAddBlock = () => {
    if (fields.length < MAX_FOREIGN_NON_TAX_RESIDENCIES) {
      append({ ...DEFAULT_FOREIGN_NON_TAX_RESIDENCY_BLOCK });
    }
  };

  return (
    <TaxInfoSubSection>
      <TaxInfoQuestionLabel variant="body1Medium">
        {t('taxInfoDrawer.question2.foreignNonTaxResidency.title', {
          tenantCountry: translatedCountry[tenantCountryCode] ?? '',
        })}
      </TaxInfoQuestionLabel>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        {t('taxInfoDrawer.question2.foreignNonTaxResidency.subtitle', {
          count: MAX_FOREIGN_NON_TAX_RESIDENCIES,
        })}
      </Typography>

      {fields.map((field, index) => (
        <ForeignNonTaxResidencyBlock
          key={field.id}
          index={index}
          total={fields.length}
          control={control}
          errors={errors}
          countryOptions={countryOptions}
          isPending={isPending}
          resetField={resetField}
          onDelete={() => remove(index)}
        />
      ))}

      {fields.length < MAX_FOREIGN_NON_TAX_RESIDENCIES && (
        <TaxInfoAddBlockRow>
          <Button
            variant="text"
            startIcon={<Icon name="plus" />}
            onClick={handleAddBlock}
            disabled={isPending}
            {...getDataTestId('tax-info-foreign-non-tax-residency-add-country')}
          >
            {t('taxInfoDrawer.question2.foreignNonTaxResidency.addCountry')}
          </Button>
        </TaxInfoAddBlockRow>
      )}
    </TaxInfoSubSection>
  );
}
